import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import NumberRating from 'components/Common/NumberRating'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import OfferDescription from '../OfferDescription'
import OfferPagePropertyTitle from '../OfferPagePropertyTitle'
import OfferPromotions from '../OfferPromotions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Image from 'components/Common/Image'
import OfferPageIntroInclusions from '../OfferPageIntroInclusions'
import Divider from 'components/Luxkit/Divider'

const OfferIntroSection = styled(BodyText)`
  grid-row: 4;

  ${mediaQueryUp.tablet} {
    grid-row: 3;
    grid-column: 1;
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
  }
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const WatchVideoButton = styled(TextButton)`
  margin-top: ${rem(12)};
`

interface Props {
  offer: App.Offer;
  property: App.OfferProperty;
  location: string;
  onWatchVideo: () => void;
  promotions: Array<OfferPackageOptionView>;
  depositsAvailable: boolean;
  className?: string;
}

function AlwaysOnPropertyDetailsExperimentVariant(props: Props) {
  const {
    offer,
    property,
    location,
    onWatchVideo,
    promotions,
    depositsAvailable,
    className,
  } = props

  return (<>
    <Header>
      <VerticalSpacer gap={8}>
        <BodyTextBlock
          variant="medium"
          className={cn(className, { deposits: depositsAvailable })}
          data-testid="offer-location"
          startIcon={<LineMapMarkerIcon />}
        >
          {location} • <TextLink href="#getting-there">view map</TextLink>
        </BodyTextBlock>
        <OfferPagePropertyTitle propertyName={property.name} />
        <Subtitle variant="subtitle2" as="h2">{offer.name}</Subtitle>

        {showUserReviewsRating(property.rating, property.reviewsTotal) && (
          <NumberRating
          link
          inlineLabel
          variant="small"
          total={property.reviewsTotal}
          rating={property.rating}
          ratingSubtitle={property.reviewsSource === 'google' ? 'From online reviews' : undefined}
        />
        )}
      </VerticalSpacer>
      {property.logoImageId &&
        <PropertyLogo
        dpr={2}
        height={75}
        enhancedFormats={false}
        id={property.logoImageId}
        alt={`${property.name || 'Vendor'} logo`}
      />}
    </Header>
    {offer.badge && <OfferBadgeTag badge={offer.badge} />}

    <OfferIntroSection variant="large">
      {offer.tileInclusions && offer.tileInclusions.length > 2 && <>
        <OfferPageIntroInclusions tileInclusionsList={offer.tileInclusions} heading="Your handpicked inclusions:" />
        <Divider kind="primary" />
      </>}
      {offer.description &&
        <OfferDescription
          description={offer.description}
          additionalDescription={offer.additionalDescription}
          offer={offer}
          offerPageTruncateText
        />
        }
      {offer.vimeoVideoId && <WatchVideoButton kind="secondary" startIcon={ <SolidPlayIcon />} onClick={onWatchVideo}>
        Play video
      </WatchVideoButton>}
      {promotions.length > 0 && <OfferPromotions offer={offer} promotions={promotions} />}
    </OfferIntroSection>
  </>)
}

export default AlwaysOnPropertyDetailsExperimentVariant
